/**
 * The optics bench, as a scene target: one object and one thin lens.
 *
 * See docs/FeatureDesigns/question-scenes/question-scenes-target.md for the design.
 *
 * Deliberately narrow. The bench has mirrors, prisms, blocks and two-lens
 * systems; this vocabulary has one lens and one object, because the first job
 * is to learn whether questions compile at all, and every part added to the
 * vocabulary is a new way for a compile to be wrong.
 *
 * Distances in the setup are magnitudes and the builder places them: the lens
 * at the rail's zero mark, the object to its left. Signs belong to the
 * readouts, in New Cartesian, because a question's sign convention is part of
 * what it is testing.
 */
import * as O from '../optics';
import { BenchManifest, BenchSolution, Reading, ReadoutDecl } from './types';

export interface OpticsSetup {
    lens: { type: 'convex' | 'concave'; f: number } | { power: number };
    object: { distance: number; height?: number };
}

const DEFAULT_HEIGHT = 2;

/** Signed focal length, cm: positive converges. */
export function focalOf(s: OpticsSetup): number {
    if ('power' in s.lens) return 100 / s.lens.power;
    return s.lens.type === 'convex' ? Math.abs(s.lens.f) : -Math.abs(s.lens.f);
}

function part(key: string, id: string, at: O.Vec2, data: Record<string, unknown>): O.Part {
    const base = O.kindOf(key).create(at, id) as unknown as O.Part;
    return { ...base, data: { ...base.data, ...data } };
}

export function buildOptics(s: OpticsSetup): O.World {
    const height = s.object.height ?? DEFAULT_HEIGHT;
    const object = part('object', 'object-1', { x: -Math.abs(s.object.distance), y: 0 }, { form: 'arrow', length: height, traced: [1] });
    const lens = part('lens', 'lens-1', { x: 0, y: 0 }, {
        hold: 'f',
        f: focalOf(s),
        // Wide enough that the object's own construction rays reach it.
        aperture: Math.max(14, Math.ceil(height * 2.5)),
    });
    return O.fitRail({ ...O.emptyWorld(), parts: [object, lens] });
}

const num = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function validateOptics(raw: unknown): string[] {
    const errors: string[] = [];
    if (!raw || typeof raw !== 'object') return ['setup must be an object'];
    const s = raw as Record<string, unknown>;
    const lens = s.lens as Record<string, unknown> | undefined;
    if (!lens || typeof lens !== 'object') errors.push('lens must be { type: "convex" | "concave", f } or { power }');
    else if ('power' in lens) {
        if (!num(lens.power) || lens.power === 0 || Math.abs(lens.power) > 100) errors.push('lens.power must be a non-zero number of dioptres, positive for convex');
    } else {
        if (lens.type !== 'convex' && lens.type !== 'concave') errors.push('lens.type must be "convex" or "concave"');
        if (!num(lens.f) || lens.f <= 0 || lens.f > 500) errors.push('lens.f must be the focal length in cm, as a positive magnitude — the type carries the sign');
    }
    const object = s.object as Record<string, unknown> | undefined;
    if (!object || typeof object !== 'object') errors.push('object must be { distance, height? }');
    else {
        if (object.distance === 'infinity') errors.push('an object at infinity is not supported yet → cannot, needs-unsupported-feature');
        else if (!num(object.distance) || object.distance <= 0 || object.distance > 1500) {
            errors.push('object.distance must be the distance from the lens in cm, as a positive magnitude, at most 1500');
        }
        if (object.height !== undefined && (!num(object.height) || object.height <= 0 || object.height > 40)) {
            errors.push('object.height must be a positive number of cm, at most 40');
        }
    }
    return errors;
}

function solveOptics(s: OpticsSetup): BenchSolution {
    const w = buildOptics(s);
    const a = O.analyse(w);
    const img = a.images.find(i => i.partId === 'object-1');
    const problems = a.problems.filter(p => p.level === 'stops').map(p => p.text);
    const f = focalOf(s);
    if (!img) return { readings: {}, problems: [...problems, 'the bench found no image report for the object'] };

    const stage = img.stages[img.stages.length - 1];
    const tip = img.points.find(p => p.t === 1);
    const atInfinity = tip?.kind === 'at-infinity' || !Number.isFinite(stage?.v ?? NaN);
    const m = img.magnification ?? stage?.m;
    const readings: Record<string, Reading> = {
        u: stage ? stage.u : -Math.abs(s.object.distance),
        v: atInfinity || !stage ? null : stage.v,
        f,
        power: 100 / f,
        m: atInfinity || m === undefined ? null : m,
        imageHeight: atInfinity || img.height === undefined ? null : img.height,
        nature: atInfinity ? 'at-infinity' : tip?.kind === 'real' || tip?.kind === 'virtual' ? tip.kind : null,
        orientation: atInfinity || img.erect === undefined ? null : img.erect ? 'erect' : 'inverted',
        size: atInfinity || m === undefined ? null : Math.abs(Math.abs(m) - 1) < 1e-6 ? 'same' : Math.abs(m) > 1 ? 'magnified' : 'diminished',
    };
    if (!img.applies && img.why) problems.push(img.why);
    return { readings, working: img.working, problems };
}

const READOUTS: ReadoutDecl[] = [
    { key: 'u', label: 'Object distance, New Cartesian (negative: the object is on the left)', unit: 'cm' },
    { key: 'v', label: 'Image distance, New Cartesian (positive: real, on the right)', unit: 'cm' },
    { key: 'f', label: 'Focal length, New Cartesian (positive: convex)', unit: 'cm' },
    { key: 'power', label: 'Power of the lens', unit: 'D' },
    { key: 'm', label: 'Magnification v ÷ u (negative: inverted)', unit: '' },
    { key: 'imageHeight', label: 'Height of the image (negative: inverted)', unit: 'cm' },
    { key: 'nature', label: 'Nature of the image', categories: ['real', 'virtual', 'at-infinity'] },
    { key: 'orientation', label: 'Erect or inverted', categories: ['erect', 'inverted'] },
    { key: 'size', label: 'Size compared with the object', categories: ['magnified', 'diminished', 'same'] },
];

const VOCABULARY = `Optics bench — setup vocabulary v1: ONE object and ONE thin lens. Units: cm, dioptres.

setup:
  { "lens": { "type": "convex" | "concave", "f": cm },       // f as a positive magnitude; the type carries the sign
    "object": { "distance": cm, "height"?: cm } }            // distance from the lens, positive magnitude; the object is on the left
  or "lens": { "power": D }                                   // signed dioptres: +2.5 D is convex, f = 40 cm

Readouts use the New Cartesian convention: u is negative, v is positive for a real image on the right,
f is positive for a convex lens, m = v/u is negative for an inverted image.
Write the answer key in the same convention: "the image is 20 cm in front of the lens, on the same side" is v = -20.

Not supported → cannot:
  - mirrors, prisms, glass slabs, two lenses, the eye → outside-bench
  - an object at infinity, a thick lens, the lensmaker's formula → needs-unsupported-feature

Inverse: when u, v or m is given and f or the object distance is asked ("an object 20 cm from a lens gives an image
at 60 cm on the other side; find f"), give the lens a trial f and add
"inverse": { "param": "lens.f", "readout": "v", "equals": 60, "range": [1, 19.9] }, and ask for the readout that
answers it ("f" — signed, so a concave lens's is negative; "u" for the object distance).
Choose the range so the readout changes one way only across it — never across the focal point.

Example — "An object 4 cm tall is 25 cm from a convex lens of focal length 15 cm. Find the position, size and nature of the image.":
  { "lens": { "type": "convex", "f": 15 }, "object": { "distance": 25, "height": 4 } }
  asks: [{ "readout": "v" }, { "readout": "imageHeight" }, { "readout": "nature" }, { "readout": "orientation" }]`;

export const opticsBench: BenchManifest<OpticsSetup, O.World> = {
    bench: 'optics',
    title: 'Optics bench',
    specVersion: 1,
    covers: [
        '966561c1-8251-45ea-8782-e018c480c02d', // Light → Lenses
        'd754c1dc-7235-4635-8150-e53ac9ebe99b', // Ray Optics → Lens Formula & Magnification
        '0eff9de1-bbe4-4231-9b47-d7fc3aac9a18', // Ray Optics → Power of a Lens
    ],
    exact: true,
    vocabulary: VOCABULARY,
    readouts: READOUTS,
    assumptions: ['thin lens', 'paraxial rays', 'air around the lens'],
    validate: validateOptics,
    build: buildOptics,
    solve: solveOptics,
};
